import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import logo from "../../assets/logo.192.png";
import CalendarConversation from "../../components/CalendarConversation/CalendarConversation";
import LocalAppConfig from "../../components/LocalAppConfig/LocalAppConfig";
import SignInWithGoogleButton from "../../components/SignInWithGoogleButton/SignInWithGoogleButton";
import config from "../../config/config.json";
import { useDocumentTitle } from "../../hooks/use-document-title";
import { useLocalAppConfig } from "../../hooks/use-local-app-config";
import { createCalendarEvent } from "../../lib/create-calendar-event";
import { CalendarEvent } from "../../types/CalendarEvent";
import { Plan } from "../../types/Plan";
import "./HomePage.scss";

function HomePage() {
  useDocumentTitle("Automate your calendar");
  const [showCalendarConversation, setShowCalendarConversation] =
    useState(false);

  const { accessToken, setAccessToken, signedIn, plan } = useLocalAppConfig();
  const handleAuth = useCallback(
    (newAccessToken: string) => {
      setAccessToken(newAccessToken);
    },
    [setAccessToken],
  );

  const handleCalendarConversationClick = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    setShowCalendarConversation(true);
  }, [accessToken]);

  const handleEarningsEventsClick = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    window.alert(
      "This is just going to create an event in your calendar called: 'Test Earnings Event' for tomorrow at 4pm EST",
    );
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(16, 0, 0, 0);

    const testEvent: CalendarEvent = {
      summary: "Test Earnings Event",
      description: "This is just a test event",
      start: {
        dateTime: tomorrow.toISOString(),
        timeZone: "America/New_York",
      },
      end: {
        dateTime: tomorrow.toISOString(),
        timeZone: "America/New_York",
      },
    };
    const { success, data } = await createCalendarEvent(accessToken, testEvent);
    if (success) {
      toast.success("Event created successfully");
      window.setTimeout(() => {
        window.open(data?.htmlLink ?? "https://calendar.google.com/");
      }, 1000);
    } else {
      window.alert("Event creation failed");
    }
  }, [accessToken]);

  const uses = [
    {
      key: "earnings-events",
      name: "Automatically add earnings events to your calendar from a list of tickers",
      description:
        "Select a list of tickers you'd like to track and we'll automaticlaly add earnings events for those companies to your calendar.",
      enabled: signedIn && [Plan.Free, Plan.Pro].includes(plan),
      clickHandler: handleEarningsEventsClick,
      cta: null,
    },
    {
      key: "calendar-stats",
      name: "Ask an agent questions about your calendar",
      description:
        "Ask an agent questions about your calendar and it will use the data in your Google Calendar to answer your questions.",
      enabled: signedIn && [Plan.Pro].includes(plan),
      cta: "Upgrade to Pro to enable calendar conversations",
      clickHandler: handleCalendarConversationClick,
    },
  ];

  const showMainContent = signedIn && showCalendarConversation;

  return (
    <div className="home-page-container">
      <div className="sidebar">
        <header>
          <div className="header-logo">
            <img src={logo} alt="Calendar Automation" />
          </div>
          <div>
            <div>Calendar Automation</div>
            <div className="header-metadata">v{config.version}</div>
          </div>
        </header>
        <div className="bubble-box">
          <header>What calendar automation is possible?</header>
          <section>
            Below is a list of all the automation available. The first step is
            to sign in with your Google account to authorize{" "}
            <strong>Calendar Automation</strong> to work its magic.
          </section>
        </div>
        <div className="uses">
          {uses.map((use) => (
            <div className="use" key={use.key}>
              <div className="use-name">{use.name}</div>
              <div className="use-description">{use.description}</div>
              {signedIn ? (
                <div>
                  <button
                    className="btn btn-primary"
                    disabled={!use.enabled}
                    onClick={use.clickHandler}
                  >
                    Get Started
                  </button>
                  {!use.enabled && <div className="cta">{use.cta}</div>}
                </div>
              ) : (
                <SignInWithGoogleButton
                  text="Sign in"
                  onSignInSuccess={handleAuth}
                />
              )}
            </div>
          ))}
        </div>
        <LocalAppConfig />
      </div>
      {showMainContent && (
        <div className="main-content">
          {showCalendarConversation && (
            <CalendarConversation
              onClose={() => setShowCalendarConversation(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default HomePage;
